import { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { yupResolver } from '@hookform/resolvers/yup';
// Components
import { PageTemplate } from 'src/shared/templates/pageTemplate';
import ControlledTextInput from 'src/shared/components/form/controlledTextInput';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import { BoxSaveCancelButtons } from 'src/shared/components/organisms/boxSaveCancelButtons';
import ControlledPushNoticeCheckbox from 'src/shared/components/form/controlledPushNoticeCheckbox';
import SegmentLocationRoleTags from 'src/shared/components/form/segmentLocationRoleTags';
import ConfirmDialogModal from 'src/shared/components/atoms/confirmDialog';
import { extraMenu } from 'src/shared/components/navigations/menuList';
import LabelWithTooltip from 'src/shared/components/organisms/labelWithToolTip';
// Services
import { ContentPageResponse } from 'src/shared/services/api/response/contentPageResponse';
import { ContentPageAPI } from 'src/shared/services/api/contentPageAPI';
import ConsoleHelper from 'src/shared/helpers/consoleHelper';
import { IParams } from 'src/shared/interfaces/params';
import { ContentTypeEnum } from 'src/shared/enums/contentType.enum';
import { IContentPage } from 'src/shared/interfaces/contentPageInterface';
import { ContentPageSchema } from 'src/shared/schemas/contentPageSchema';
import { RootStateType } from 'src/shared/services/redux/reducers';
import {
  getBusinessAreas,
  locationsTags,
  rolesTags,
} from 'src/shared/helpers/filteredTags';
import { getMenuListAction } from 'src/shared/services/redux/actions/getMenuList';
import { TagsResponse } from 'src/shared/services/api/response/tagsResponse';
import ControlledSwitchNorway from 'src/shared/components/form/controlledSwitchNorway';

const EditNewsListPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<IParams>();
  const dispatch = useDispatch();

  // Global states
  const { userCompany } = useSelector(
    (state: RootStateType) => state.authState,
  );

  // Local states
  const [loading, setLoading] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [pageToEdit, setPageToEdit] = useState<ContentPageResponse>();

  const formMethods = useForm<IContentPage>({
    resolver: yupResolver(ContentPageSchema(t)),
    defaultValues: {
      title: '',
      sortIndex: '',
      contentType: ContentTypeEnum.NewsListPage,
      businessAreaTags: [],
      locations: [],
      roles: [],
      canHaveChildren: true,
      sendNotice: false,
      sendEmail: false,
      publishDate: dayjs().format('YYYY-MM-DD'),
      companyId2: null,
    },
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = formMethods;

  // Hämta nyhetslistan som ska editeras för att sätta formstates
  const getNewsListPageDetails = useCallback(async () => {
    try {
      const newsAPI = new ContentPageAPI();
      setLoading(true);
      const res = await newsAPI.Get(id);
      setPageToEdit(res?.result);
      reset({
        title: res?.result?.title,
        sortIndex: res?.result?.sortIndex?.toString(),
        contentType: res?.result?.contentType,
        canHaveChildren: true,
        companyId: res?.result?.companyId,
        businessAreaTags: getBusinessAreas(res?.result?.businessAreaTags),
        locations: locationsTags(res?.result?.tags),
        roles: rolesTags(res?.result?.tags),
        sendNotice: false,
        publishDate: dayjs(res?.result?.publishDate).format('YYYY-MM-DD'),
        companyId2: res?.result?.companyId2 ?? null,
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      ConsoleHelper.log(error ?? 'Unkown error: getNewsListPageDetails');
    }
  }, [id, reset]);

  useEffect(() => {
    getNewsListPageDetails();
  }, [getNewsListPageDetails, id]);

  const formSubmit: SubmitHandler<IContentPage> = async (
    data: IContentPage,
  ) => {
    const newData = data;
    setLoading(true);
    const businessAreaTagIDs: number[] = [];
    const tagIDs: number[] = [];
    if (newData?.locations) {
      newData.locations.forEach((element) => {
        if (element.id) {
          tagIDs.push(element.id);
        }
      });
    }
    if (newData?.roles) {
      newData.roles.forEach((element) => {
        if (element.id) {
          tagIDs.push(element.id);
        }
      });
    }
    if (data.businessAreaTags) {
      data.businessAreaTags.forEach((tag: TagsResponse) => {
        if (tag.id) {
          businessAreaTagIDs.push(tag?.id);
        }
      });
    }
    newData.businessAreaTags = businessAreaTagIDs;
    newData.tags = tagIDs;
    newData.publishDate = dayjs(data.publishDate).format('YYYY-MM-DD');
    newData.canHaveChildren = data.canHaveChildren;
    newData.companyId2 = data.companyId2; // Publicera till norska bolag från Instalco SE
    newData.content = '';
    if (
      pageToEdit?.parentContentPageId &&
      pageToEdit?.parentContentPageId !== 0
    )
      newData.parentContentPageId = pageToEdit?.parentContentPageId;
    ConsoleHelper.log('newData', newData);
    try {
      const newsAPI = new ContentPageAPI();
      await newsAPI.updateNewsWithID(id, newData);
      dispatch(getMenuListAction(extraMenu(t), userCompany?.id));
      setLoading(false);
      toast.success(t('Done!'), {
        theme: 'colored',
      });
      reset();
      history.push(`/NewsListPage/${pageToEdit?.id}`);
    } catch (error) {
      ConsoleHelper.log('error ', error);
      setLoading(false);
      toast.error(t('SomethingWentWrong'));
    }
  };

  const deleteNews = () => {
    setShowDeleteDialog(true);
  };

  // Radera nyhetlista mot API
  const removeNews = async () => {
    setLoading(true);
    try {
      const newsAPI = new ContentPageAPI();
      if (id !== undefined) {
        await newsAPI.deleteItemWithQueryID(Number(id));
        setLoading(false);
        setShowDeleteDialog(false);
        toast.success(t('Deleted!'), {
          theme: 'colored',
        });
        reset();
        history.go(-2);
      }
    } catch (error) {
      setLoading(false);
      setShowDeleteDialog(false);
      toast.error(t('SomethingWentWrong'));
    }
  };
  return (
    <PageTemplate
      header={t('EditNewsList')}
      headerContent=''
      content={
        <Box sx={{ p: 1 }}>
          {loading && <LinearProgress />}
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(formSubmit)}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                <ControlledTextInput
                  name='title'
                  label={t('Title')}
                  type='text'
                />
                <LabelWithTooltip
                  label={t('PushContentTo')}
                  infoContent={t('TooltipPushContentTo')}
                />
                <SegmentLocationRoleTags
                  companyId={pageToEdit?.companyId ?? 0}
                  control={control}
                  errors={errors}
                />
                <ControlledPushNoticeCheckbox control={control} />
                <ControlledTextInput
                  name='sortIndex'
                  label={t('MenuSortingOrder')}
                  type='text'
                />
                <ControlledDatePicker
                  label={t('PublishDate')}
                  name='publishDate'
                />
                <ControlledSwitchNorway name='companyId2' />
                <BoxSaveCancelButtons
                  onCancel={() => reset()}
                  linkTo={`/NewsListPage/${id}`}
                  saveDisabled={loading}
                  showDeleteButton={false}
                  onDelete={deleteNews}
                />
              </Box>
            </form>
          </FormProvider>
          <ConfirmDialogModal
            open={showDeleteDialog}
            dialogTitle={t('Confirm')}
            dialogText={t('ConfirmDeleting')}
            handleClose={() => setShowDeleteDialog(false)}
            confirmButtonClick={() => removeNews()}
            confirmTextButton={t('OK')}
            cancelTextButton={t('Cancel')}
          />
        </Box>
      }
    />
  );
};

export default EditNewsListPage;
